import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from '../ui/badge';
import { Calculator, CreditCard, Zap, AlertCircle, CheckCircle } from 'lucide-react';

interface CostEstimate {
  totalPages: number;
  processedPages: number;
  costCents: number;
  costDollars: number;
  description: string;
}

interface BillingBreakdown {
  canProcess: boolean;
  trialPagesUsed: number;
  paidPagesNeeded: number;
  costCents: number;
  costDollars: number;
}

interface UserAccess {
  type: 'access_code' | 'trial' | 'pay_per_use';
  remainingTrialPages: number;
}

interface CostEstimatorProps {
  projectId: string;
  pageRange: {
    startPage: number;
    endPage?: number;
    endnotesEnabled: boolean;
    endnotesStartPage?: number;
  };
  onEstimateReady?: (estimate: { costEstimate: CostEstimate; billingBreakdown: BillingBreakdown; userAccess: UserAccess }) => void;
  className?: string;
}

export const CostEstimator: React.FC<CostEstimatorProps> = ({ 
  projectId, 
  pageRange, 
  onEstimateReady,
  className = '' 
}) => {
  const [estimate, setEstimate] = useState<{
    costEstimate: CostEstimate;
    billingBreakdown: BillingBreakdown;
    userAccess: UserAccess;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounced fetch function to prevent flickering
  const debouncedFetchCostEstimate = useCallback(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Only show loading on first load
    if (isFirstLoad) {
      setIsLoading(true);
    }
    // Don't show loading state for subsequent updates to prevent flickering
    
    // Debounce the fetch
    debounceTimerRef.current = setTimeout(() => {
      fetchCostEstimate();
    }, isFirstLoad ? 0 : 300); // Immediate first load, 300ms debounce for updates
  }, [projectId, pageRange, isFirstLoad]); // Removed 'estimate' to prevent infinite loop

  useEffect(() => {
    debouncedFetchCostEstimate();
    
    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedFetchCostEstimate]);

  const fetchCostEstimate = async () => {
    try {
      // Abort previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      setError(null);

      const { getApiUrl } = await import('../lib/config');
      const token = localStorage.getItem('orca_auth_token');
      
      if (!token) {
        setError('Not authenticated');
        setIsLoading(false);
        return;
      }

      const response = await fetch(getApiUrl(`/api/projects/${projectId}/estimate-cost`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pageRange),
        signal: abortControllerRef.current.signal
      });

      if (response.ok) {
        const data = await response.json();
        setEstimate(data);
        onEstimateReady?.(data);
        setIsFirstLoad(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to estimate cost');
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError('Network error while estimating cost');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && isFirstLoad) {
    return (
      <div className={`bg-card/50 border border-border/50 rounded-lg p-4 transition-all duration-200 ${className}`}>
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="animate-spin text-muted-foreground" size={16} />
          <span className="text-sm font-medium text-muted-foreground">Calculating cost...</span>
        </div>
        <div className="animate-pulse space-y-2">
          <div className="h-3 bg-muted/50 rounded w-3/4"></div>
          <div className="h-3 bg-muted/50 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-destructive/10 border border-destructive/30 rounded-lg p-4 transition-all duration-200 ${className}`}>
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle size={16} />
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }

  if (!estimate) return null;

  const { costEstimate, billingBreakdown, userAccess } = estimate;

  // Access code users (unlimited)
  if (userAccess.type === 'access_code') {
    return (
      <div className={`bg-card/60 border border-green-500/30 rounded-lg p-4 transition-all duration-300 ${className}`}>
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle className="text-green-400" size={16} />
          <h3 className="text-sm font-medium text-foreground">Cost Estimate</h3>
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs px-2 py-0.5">FREE</Badge>
        </div>
        
        <div className="space-y-2 text-sm text-muted-foreground">
          <div className="flex justify-between items-center">
            <span>Total pages to process:</span>
            <strong className="text-foreground">{costEstimate.processedPages}</strong>
          </div>
          <div className="flex justify-between items-center font-medium">
            <span>Total cost:</span>
            <strong className="text-green-400">FREE</strong>
          </div>
          <div className="text-xs text-green-400/80 mt-2 pt-2 border-t border-border/50">
            ✓ Access code active - unlimited processing
          </div>
        </div>
      </div>
    );
  }

  // Trial or pay-per-use users
  const hasTrialPages = billingBreakdown.trialPagesUsed > 0;
  const needsPayment = billingBreakdown.paidPagesNeeded > 0;

  return (
    <div className={`${needsPayment ? 'bg-card/60 border-orange-500/30' : 'bg-card/60 border-blue-500/30'} border rounded-lg p-4 transition-all duration-300 ${isLoading && !isFirstLoad ? 'opacity-75' : 'opacity-100'} ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <Calculator className={`${needsPayment ? 'text-orange-400' : 'text-blue-400'} ${isLoading && !isFirstLoad ? 'animate-spin' : ''}`} size={16} />
        <h3 className="text-sm font-medium text-foreground">
          Cost Estimate
        </h3>
{userAccess.type === 'trial' && userAccess.remainingTrialPages > 0 && (
          <Badge className={`text-xs px-2 py-0.5 ${needsPayment ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'}`}>
            TRIAL
          </Badge>
        )}
      </div>

      <div className="space-y-2 text-sm text-muted-foreground">
        <div className="flex justify-between items-center">
          <span>Total pages to process:</span>
          <strong className="text-foreground">{costEstimate.processedPages}</strong>
        </div>
        
        {hasTrialPages && (
          <div className="flex justify-between items-center">
            <span>Free trial pages used:</span>
            <strong className="text-blue-400">{billingBreakdown.trialPagesUsed}</strong>
          </div>
        )}
        
        {needsPayment ? (
          <div className="flex justify-between items-center font-medium">
            <span>Total cost:</span>
            <strong className="text-orange-400">${billingBreakdown.costDollars.toFixed(2)}</strong>
          </div>
        ) : (
          <div className="flex justify-between items-center font-medium">
            <span>Total cost:</span>
            <strong className="text-blue-400">FREE</strong>
          </div>
        )}
      </div>

      {needsPayment && (
        <div className="mt-3 pt-2 border-t border-border/50">
          <div className="flex items-center gap-2 text-xs text-orange-400/80">
            <CreditCard size={12} />
            <span>Payment will be processed when you start the workflow</span>
          </div>
        </div>
      )}

      {userAccess.type === 'trial' && userAccess.remainingTrialPages > billingBreakdown.trialPagesUsed && (
        <div className="mt-3 pt-2 border-t border-border/50">
          <div className="flex items-center gap-2 text-xs text-blue-400/80">
            <Zap size={12} />
            <span>
              {userAccess.remainingTrialPages - billingBreakdown.trialPagesUsed} trial pages will remain after this project
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostEstimator;
